import { Stack, Typography } from "@mui/material";
import { Button, CircularLoader, InfoIcon, Input } from "components";
import {
  useCreateDattoAuthConfig,
  useGetDattoAuthConfig,
  useUpdateDattoAuthConfig,
} from "controller";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { addMixpanelEvent } from "utilz/helpers";
import { CopyWebhookUrlAndChooseDefaultBoard } from "./CopyWebhookUrlAndChooseDefaultBoard";

export const ConnectDattoAccount = ({
  apiKey,
  setApiKey,
  apiSecretKey,
  setApiSecretKey,
  apiUrl,
  setApiUrl,
  chooseBoardRef,
  onWebhookGenerated,
}: {
  apiKey: string;
  setApiKey: any;
  apiSecretKey: string;
  setApiSecretKey: any;
  apiUrl: string;
  setApiUrl: any;
  chooseBoardRef: any;
  onWebhookGenerated: () => void;
}) => {
  const { data: dattoAuthConfig, isLoading: isDattoAuthConfigLoading } =
    useGetDattoAuthConfig();
  const {
    mutateAsync: createDattoAuthConfig,
    isLoading: isCreateDattoAuthConfigLoading,
  } = useCreateDattoAuthConfig();
  const {
    mutateAsync: updateDattoAuthConfig,
    isLoading: isUpdateDattoAuthConfigLoading,
  } = useUpdateDattoAuthConfig();

  const [errorMessage, setErrorMessage] = useState("");

  const onGenerateWebhookUrl = async () => {
    try {
      if (dattoAuthConfig?.data?.api_key) {
        await updateDattoAuthConfig({
          api_key: apiKey,
          api_secret_key: apiSecretKey,
          api_url: apiUrl,
          board: dattoAuthConfig?.data?.board,
        });
      } else {
        await createDattoAuthConfig({
          api_key: apiKey,
          api_secret_key: apiSecretKey,
          api_url: apiUrl,
        });
        addMixpanelEvent({
          event: "datto_connection_success",
        });
        onWebhookGenerated?.();
      }
      toast.success("Webhook secret generated successfully");
    } catch (error) {
      toast.error("Failed to generate webhook secret.");
    }
  };

  useEffect(() => {
    if (errorMessage && apiKey) {
      setErrorMessage("");
    }
  }, [apiKey]);

  return (
    <Stack flex={1} alignItems={"center"}>
      {isDattoAuthConfigLoading ? (
        <Stack alignItems={"center"} justifyContent={"center"} p="24px">
          <CircularLoader size={24} />
        </Stack>
      ) : (
        <Stack gap="24px" p="24px" width="100%">
          {dattoAuthConfig?.data?.is_connected === true ? (
            <CopyWebhookUrlAndChooseDefaultBoard
              chooseBoardRef={chooseBoardRef}
            />
          ) : (
            <>
              <DattoIntroContent />
              <Stack gap="12px">
                <Input
                  label="Datto API URL"
                  placeholder="Enter API URL"
                  name="datto_platform"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  errorMessage={errorMessage}
                />
                <Input
                  label="Datto RMM API key"
                  placeholder="Enter API key"
                  name="datto_api_key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  errorMessage={errorMessage}
                />
                <Input
                  label="Datto RMM API secret key"
                  placeholder="Enter API secret key"
                  name="datto_api_secret_key"
                  value={apiSecretKey}
                  onChange={(e) => setApiSecretKey(e.target.value)}
                  errorMessage={errorMessage}
                />
                <Button
                  sx={{
                    alignSelf: "flex-start",
                  }}
                  onClick={onGenerateWebhookUrl}
                  loading={
                    isCreateDattoAuthConfigLoading ||
                    isUpdateDattoAuthConfigLoading
                  }
                  disabled={!apiUrl || !apiKey || !apiSecretKey}
                >
                  Generate webhook URL
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const DattoIntroContent = () => {
  return (
    <Stack gap="16px">
      <Typography variant="h6">
        Enter your Datto RMM account details to start the integration
      </Typography>
      <Stack
        p="12px 28px 12px 16px"
        border="1px solid #98C6F1"
        borderRadius="8px"
        bgcolor="#E8F3FE"
        direction="row"
        alignItems={"flex-start"}
        gap="8px"
      >
        <InfoIcon
          sx={{
            color: "#0075C8",
          }}
        />
        <Stack gap="8px">
          <Typography variant="subtitle1" color="#0075C8">
            Generate the API key from a Datto Admin user account and paste the
            details here.
          </Typography>
          <Typography variant="body-small">
            Datto account {">"} Setup {">"} Users {">"} User details {">"}{" "}
            Generate API key
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
